import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import celebrate from '../utils/celebrate';
import PageArrival from '../components/PageArrival';
import { FolderLock as A1, Upload as A2, BadgeCheck as A3, Download as A4, ShieldCheck as A5, FileText as A6 } from 'lucide-react';

import { AppLayout, GlassCard, PageHeader, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FileText, Upload, Trash2, Tag, File, Calendar, Eye, Download } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { showFriendlyError } from '../utils/friendlyErrors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VaultPage() {
  const [arrived, setArrived] = useState(false);
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/vault/documents`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name);
    formData.append('doc_type', 'transcript');
    formData.append('tags', '');

    setUploading(true);
    try {
      const response = await axios.post(`${API}/vault/upload`, formData, {
        ...getAuthHeader(),
        withCredentials: true,
        headers: {
          ...getAuthHeader().headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      setDocuments([response.data, ...documents]);
      celebrate('Filed. That is proof you will never have to hunt for again.');
    } catch (error) {
      console.error('Upload error:', error);
      showFriendlyError(error, { fallback: 'Failed to upload document' });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (docId) => {
    try {
      await axios.delete(`${API}/vault/documents/${docId}`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setDocuments(documents.filter(d => d.id !== docId));
      toast.success('Document deleted');
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const docTypeIcons = {
    transcript: FileText,
    resume: File,
    certification: Tag,
    test_score: FileText
  };

  const docTypeColors = {
    transcript: 'blue',
    resume: 'emerald',
    certification: 'amber',
    test_score: 'purple'
  };

  const filteredDocs = filter === 'all' 
    ? documents 
    : documents.filter(d => d.doc_type === filter);

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (!arrived) return (
    <AppLayout>
      <PageArrival
        testid="vault-arrival"
        eyebrow="My Record"
        title="Everything that proves it, in one place."
        subtitle="Transcripts, certificates, licences, awards, references and résumés, stored privately, ready the moment someone asks, and attached to the skills they prove."
        accent="#34d399"
        second="#60a5fa"
        ctaLabel="Open my record"
        narration={"This is My Record. Every application asks for proof at the worst possible moment, so keep it here: transcripts, certificates, licences, awards, references and résumés. It is private to you, downloadable and deletable at any time, and each document can be attached to the skill it proves so your record and your evidence stay together."}
        facts={[
          { icon: A5, head: "Private to you", sub: "Yours to download or delete any time" },
          { icon: A3, head: "Attached to skills", sub: "Each document proves something specific" },
          { icon: A4, head: "Ready when asked", sub: "No scramble the night before a deadline" }
        ]}
        modules={[
          { icon: A2, label: "Upload anything", line: "Transcripts, certificates, licences, awards", hue: "#34d399" },
          { icon: A6, label: "Documents", line: "Résumés and letters, versioned", hue: "#22d3ee" },
          { icon: A3, label: "Proof of skill", line: "Evidence linked to the skills it supports", hue: "#fbbf24" },
          { icon: A1, label: "Organised", line: "Sorted so you find it in seconds", hue: "#a78bfa" },
          { icon: A4, label: "Export", line: "Take a copy whenever you want", hue: "#f472b6" },
          { icon: A5, label: "Privacy", line: "You control what is shared, and with whom", hue: "#60a5fa" }
        ]}
        onStart={() => { setArrived(true); window.scrollTo({ top: 0 }); }}
      />
    </AppLayout>
  );

  return (
    <AppLayout>
      <PageHeader 
        title="Document Vault"
        description="Securely store and manage your academic documents"
        action={
          <label className="btn-primary inline-flex items-center gap-2 cursor-pointer">
            <Upload className="w-5 h-5" />
            {uploading ? 'Uploading...' : 'Upload Document'}
            <input 
              type="file" 
              className="hidden" 
              onChange={handleUpload}
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
              disabled={uploading}
              data-testid="upload-input"
            />
          </label>
        }
      />

      {/* Filter */}
      <div className="flex items-center gap-4 mb-6">
        <span className="text-slate-300 text-sm">Filter by type:</span>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40 input-dark" data-testid="doc-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Documents</SelectItem>
            <SelectItem value="transcript">Transcripts</SelectItem>
            <SelectItem value="resume">Resumes</SelectItem>
            <SelectItem value="certification">Certifications</SelectItem>
            <SelectItem value="test_score">Test Scores</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-slate-300 text-sm">{filteredDocs.length} document(s)</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : documents.length === 0 ? (
        <GlassCard className="text-center py-16">
          <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <h3 className="font-outfit text-xl font-semibold text-white mb-2">No documents yet</h3>
          <p className="text-slate-300 mb-6 max-w-md mx-auto">
            Upload your transcripts, test scores, and other documents to improve your AI recommendations.
          </p>
          <label className="btn-primary inline-flex items-center gap-2 cursor-pointer">
            <Upload className="w-5 h-5" />
            Upload Your First Document
            <input 
              type="file" 
              className="hidden" 
              onChange={handleUpload}
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
              data-testid="upload-first"
            />
          </label>
        </GlassCard>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredDocs.map((doc) => {
            const Icon = docTypeIcons[doc.doc_type] || FileText;
            const color = docTypeColors[doc.doc_type] || 'blue';
            return (
              <GlassCard key={doc.id} className="group" data-testid={`doc-${doc.id}`}>
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 bg-${color}-500/20`}>
                    <Icon className={`w-6 h-6 text-${color}-400`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-white truncate">{doc.name}</h3>
                    <p className="text-slate-300 text-sm capitalize">{doc.doc_type.replace('_', ' ')}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-slate-300">
                      <span>{formatFileSize(doc.file_size)}</span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(doc.uploaded_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
                
                {doc.extracted_text && (
                  <div className="mt-4 p-3 bg-slate-800/30 rounded-lg">
                    <p className="text-xs text-slate-300 line-clamp-3">{doc.extracted_text}</p>
                  </div>
                )}

                {doc.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {doc.tags.map((tag, i) => (
                      <span key={i} className="px-2 py-1 bg-slate-800 rounded text-xs text-slate-300">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-800/50 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white">
                    <Eye className="w-4 h-4 mr-1" />
                    View
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-red-400 hover:text-red-300 ml-auto"
                    onClick={() => handleDelete(doc.id)}
                    data-testid={`delete-${doc.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </GlassCard>
            );
          })}
        </div>
      )}
    </AppLayout>
  );
}
